<template>
    <div>
        <SoliForm
            :title="getFormTitle"
            text="Save"
            icon="save"
            ref="form"
            :error="errors"
            limitScroll
            :persistent="step >= 2"
            :saveChanges="(saveChanges || cancelAvail) && !futureConflict"
            :selection="getSelection"
            :hideBtn="hideSaveBtn"
            :extraBtn="hideSaveBtn"
            @submit="editing ? editEvent() : addEvent()"
            @reset="reset"
        >
            <template #body>
                <SoliChoice
                    v-if="futureConflict"
                    title="Hey!"
                    :subtitle="`${
                        $page.props.auth.user.id == teacher.id
                            ? 'You are '
                            : `${teacher.first_name} is `
                    } ${
                        futureConflict.recurrence_id
                            ? `already scheduled during this time, starting ${globalMomentDate(
                                  new Date(futureConflict.date)
                              )}. Would you like to schedule until then?`
                            : `unavailable at this time.`
                    }`"
                    :button1="futureConflict.recurrence_id ? 'Ok' : null"
                    @option1="
                        [
                            (form.recurrence_end = futureConflict.date),
                            (futureConflict = null),
                            (form.noConflict = true),
                            ($refs.form.loading = true),
                            editing ? editEvent() : addEvent(),
                        ]
                    "
                    :button2="!futureConflict.recurrence_id ? 'Ok' : 'Go Back'"
                    @option2="
                        [
                            (futureConflict = null),
                            (form.noConflict = false),
                            (step = 1),
                            ($refs.form.loading = false),
                        ]
                    "
                ></SoliChoice>
                <SoliChoice
                    v-if="formError"
                    title="Uh oh"
                    :subtitle="formError"
                    button1="Okay"
                    @option1="
                        closeModal(() => {
                            formError = null;
                            futureConflict = null;
                            form.noConflict = false;
                            step = 1;
                            $refs.form.loading = false;
                        })
                    "
                ></SoliChoice>
                <v-row class="pa-4" v-if="step >= 2 && step !== finalStep">
                    <div class="my-4 ml-3 soli-grey">
                        <v-progress-circular
                            class="mr-5"
                            :value="progressVal"
                            :rotate="180"
                            :size="55"
                            color="#228cbd"
                            ><div class="progress-text">
                                {{ step }} of {{ finalStep }}
                            </div></v-progress-circular
                        >{{ getStepperTitle }}
                    </div>
                </v-row>
                <v-stepper
                    v-model="step"
                    :key="whichSteps[0].steps"
                    class="elevation-0"
                    style="margin-top: -12px"
                >
                    <v-stepper-content
                        v-if="findStep('edit')"
                        :step="findStep('edit')"
                        class="pa-0"
                    >
                        <!-- Repeating -->
                        <v-switch
                            v-if="!editing"
                            v-model="form.repeating"
                            label="Repeats Weekly"
                            class="px-2"
                            dense
                            @change="updateRepeating"
                        ></v-switch>
                        <!-- online -->
                        <v-switch
                            v-model="form.online_only"
                            :disabled="
                                (editing && hasLessons && saveChanges) ||
                                (editing && hasLessons && form.online_only)
                            "
                            label="Online Only"
                            :class="[!editing ? 'mt-0' : 'my-3', 'px-2']"
                            @change="
                                hasLessons
                                    ? ((moveOnline = true),
                                      step++,
                                      getAffectedLessons())
                                    : null
                            "
                            dense
                        ></v-switch>
                        <div
                            :class="[!editing ? 'mt-0' : 'my-3', 'px-2']"
                        ></div>
                        <SoliUserLocation
                            v-if="teacher.locations.length > 1"
                            :key="form.online_only"
                            v-model="form.locations"
                            :locations="teacher.locations"
                            :disabled="
                                avail.event
                                    ? !!avail.event.extendedProps.assignments
                                          .length
                                    : false
                            "
                            :rules="rules.select"
                        />
                        <!-- Start Date -->
                        <SoliDate
                            v-if="!editing"
                            v-model="form.startDate"
                            label="Start Date"
                            :error="errors.starts_at"
                            :rules="rules.input"
                            noLimit
                        >
                        </SoliDate>
                        <!-- Start Time -->
                        <SoliTime
                            v-model="form.startTime"
                            :error-messages="errors.starts_at"
                            :rules="rules.input"
                        ></SoliTime>
                        <!-- End Time -->
                        <SoliTime
                            v-model="form.endTime"
                            label="End Time"
                            icon="timer_off"
                            :error-messages="errors.ends_at"
                            :rules="rules.input"
                        ></SoliTime>
                        <v-select
                            multiple
                            v-model="form.selectedTypes"
                            :items="lessonTypes"
                            item-text="name"
                            item-value="name"
                            label="Services"
                            prepend-inner-icon="event_available"
                            outlined
                            dense
                        ></v-select>
                        <SoliUserInstruments
                            v-model="form.instruments"
                            :instruments="teacher.instruments"
                            multiple
                        ></SoliUserInstruments>
                    </v-stepper-content>
                    <v-stepper-content
                        v-if="findStep('selectRecurring')"
                        :step="findStep('selectRecurring')"
                        class="pa-0"
                    >
                        <v-list-item>
                            <SoliBtn
                                :disabled="
                                    (hasLessons && cancelAvail) ||
                                    affectedLessonsLength > 0 ||
                                    futureLessons ||
                                    removedFromSeries
                                "
                                text="This and Future Events"
                                block
                                @click="handleSelectFuture(), step++"
                                color="soli-grey"
                                outlined
                                class="mb-3"
                            ></SoliBtn>
                        </v-list-item>
                        <v-list-item>
                            <SoliBtn
                                text="This Event"
                                block
                                @click="handleSelectSingle(), step++"
                                color="soli-grey"
                                outlined
                                class="mb-3"
                            ></SoliBtn>
                        </v-list-item>
                    </v-stepper-content>
                    <v-stepper-content
                        v-if="findStep('affected')"
                        :step="findStep('affected')"
                        class="pa-0"
                    >
                        <v-list class="pt-0">
                            <div
                                v-for="lesson in affectedLessons.filter(
                                    (e) => e.students.length && !e.cancelled
                                )"
                                :key="lesson.id"
                            >
                                <v-list-item
                                    v-for="student in lesson.students"
                                    :key="student.id"
                                >
                                    <v-list-item-content class="soli-grey">
                                        <v-row
                                            no-gutters
                                            justify="space-between"
                                            class="whitespace-nowrap flex-nowrap"
                                        >
                                            <div
                                                class="shrink flex-1 justify-start"
                                            >
                                                <small>
                                                    {{ student.email }}
                                                </small>
                                                <v-list-item-title>
                                                    {{ student.name }}
                                                </v-list-item-title>
                                                <small>{{
                                                    `${
                                                        lesson.group_event
                                                            ? lesson.group_event
                                                                  .title
                                                            : lesson.instrument
                                                                  .name
                                                    } · ${globalMomentTime(
                                                        lesson.starts_at
                                                    )}`
                                                }}</small>
                                            </div>
                                            <div
                                                class="flex grow gap-2 justify-end absolute right-0 white"
                                            >
                                                <v-btn
                                                    icon
                                                    large
                                                    class="soli-blue-text"
                                                    :href="`tel:${student.phone}`"
                                                >
                                                    <v-icon>phone</v-icon>
                                                </v-btn>
                                                <v-btn
                                                    icon
                                                    large
                                                    class="soli-blue-text"
                                                    :href="`sms:${student.phone}`"
                                                >
                                                    <v-icon>sms</v-icon>
                                                </v-btn>
                                            </div>
                                        </v-row>
                                    </v-list-item-content></v-list-item
                                >
                            </div>
                            <v-list-item
                                v-for="lesson in affectedLessons.filter(
                                    (e) => !e.students.length
                                )"
                                :key="lesson.id"
                            >
                                <v-list-item-content class="soli-grey">
                                    <v-row>
                                        <v-col>
                                            <small>{{
                                                `${
                                                    lesson.group_event_id
                                                        ? lesson.group_event
                                                              .title
                                                        : lesson.instrument
                                                        ? lesson.instrument.name
                                                        : "Hold"
                                                } · ${globalMomentTime(
                                                    lesson.starts_at
                                                )}`
                                            }}</small>
                                        </v-col>
                                    </v-row>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </v-stepper-content>

                    <v-stepper-content
                        v-if="findStep('email')"
                        :step="findStep('email')"
                        class="pa-0"
                    >
                        <v-list>
                            <v-list-item class="px-3">
                                <v-textarea
                                    v-model="emailBody"
                                    ref="email"
                                    outlined
                                    dense
                                    label="Your message"
                                    :disabled="form.skipEmail"
                                    :rules="
                                        getStepperTitle == 'Send Email?'
                                            ? emailBodyRule
                                            : []
                                    "
                                    >{{ emailBody }}</v-textarea
                                >
                            </v-list-item>
                            <v-list-item
                                class="pb-0 pt-8"
                                style="min-height: 3px"
                                ><v-divider inset
                            /></v-list-item>

                            <v-list-item class="float-right">
                                <v-switch
                                    color="red"
                                    v-model="form.skipEmail"
                                    label="No thanks"
                                ></v-switch></v-list-item
                        ></v-list>
                    </v-stepper-content>

                    <v-stepper-content
                        v-if="findStep('confirm')"
                        :step="findStep('confirm')"
                        class="pa-0"
                    >
                        <v-list class="px-1 soli-grey">
                            <div v-if="!cancelAvail">
                                <div class="mb-5">
                                    <div class="caption">Original</div>
                                    <div :class="['flex align-center mt-1']">
                                        <v-icon
                                            x-small
                                            color="#4b6876"
                                            class="mr-1"
                                            >today</v-icon
                                        >
                                        <small>{{
                                            `${formCopy.originalStartDate}${
                                                getRecurrenceEnd
                                                    ? ` until ${getRecurrenceEnd}`
                                                    : ""
                                            }`
                                        }}</small>
                                    </div>

                                    <div :class="['flex align-center mt-1']">
                                        <v-icon
                                            x-small
                                            color="#4b6876"
                                            class="mr-1"
                                            >repeat</v-icon
                                        >
                                        <small>{{
                                            `
                                            Weekly on ${globalMomentWeekDay(
                                                new Date(
                                                    formCopy.originalStartDate
                                                )
                                            )}s`
                                        }}</small>
                                    </div>
                                    <div :class="['flex align-center mt-1']">
                                        <v-icon
                                            x-small
                                            color="#4b6876"
                                            class="mr-1"
                                            >schedule</v-icon
                                        >
                                        <small>{{
                                            `${formCopy.originalStartTime} to ${formCopy.originalEndTime} `
                                        }}</small>
                                    </div>
                                    <div
                                        v-if="showInstruments"
                                        :class="['flex align-center mt-1']"
                                    >
                                        <v-icon
                                            x-small
                                            color="#4b6876"
                                            class="mr-1"
                                            >music_note</v-icon
                                        >
                                        <small>{{
                                            formCopy.originalInstruments
                                                .map((e) => e.name)
                                                .toString() + " "
                                        }}</small>
                                    </div>
                                    <div
                                        v-if="showSelectedTypes"
                                        :class="['flex align-center mt-1']"
                                    >
                                        <v-icon
                                            x-small
                                            color="#4b6876"
                                            class="mr-1"
                                            >filter_list</v-icon
                                        >
                                        <small>{{
                                            formCopy.originalSelectedTypes
                                                .map((e) => e.name)
                                                .toString()
                                        }}</small>
                                    </div>
                                    <div
                                        v-if="showOnline"
                                        :class="['flex align-center mt-1']"
                                    >
                                        <v-icon
                                            x-small
                                            color="#4b6876"
                                            class="mr-1"
                                            >{{
                                                formCopy.originalOnlineOnly
                                                    ? "videocam"
                                                    : "meeting_room"
                                            }}</v-icon
                                        >
                                        <small>{{
                                            formCopy.originalOnlineOnly
                                                ? "Online"
                                                : "In-studio"
                                        }}</small>
                                    </div>
                                </div>

                                <v-divider />

                                <div class="my-5">
                                    <div class="caption">Update</div>
                                    <div :class="['flex align-center mt-1']">
                                        <v-icon
                                            x-small
                                            color="#4b6876"
                                            class="mr-1"
                                            >today</v-icon
                                        >
                                        <small v-if="form.futureEvents">{{
                                            `${form.startDate}${
                                                getRecurrenceEnd
                                                    ? ` until ${getRecurrenceEnd}`
                                                    : " and future events"
                                            }`
                                        }}</small>
                                        <small v-if="!form.futureEvents">{{
                                            `Editing only ${form.startDate}`
                                        }}</small>
                                    </div>

                                    <div :class="['flex align-center mt-1']">
                                        <v-icon
                                            x-small
                                            color="#4b6876"
                                            class="mr-1"
                                            >repeat</v-icon
                                        >
                                        <small>{{
                                            `
                                            Weekly on ${globalMomentWeekDay(
                                                new Date(
                                                    formCopy.originalStartDate
                                                )
                                            )}s`
                                        }}</small>
                                    </div>
                                    <div :class="['flex align-center mt-1']">
                                        <v-icon
                                            x-small
                                            color="#4b6876"
                                            class="mr-1"
                                            >schedule</v-icon
                                        >
                                        <small>{{
                                            `${form.startTime} to ${form.endTime}`
                                        }}</small>
                                    </div>
                                    <div
                                        v-if="showInstruments"
                                        :class="['flex align-center mt-1']"
                                    >
                                        <v-icon
                                            x-small
                                            color="#4b6876"
                                            class="mr-1"
                                            >music_note</v-icon
                                        >
                                        <small>{{
                                            form.instruments
                                                .map((e) => e.name)
                                                .toString()
                                        }}</small>
                                    </div>
                                    <div
                                        v-if="showSelectedTypes"
                                        :class="['flex align-center mt-1']"
                                    >
                                        <v-icon
                                            x-small
                                            color="#4b6876"
                                            class="mr-1"
                                            >filter_list</v-icon
                                        >
                                        <small>{{
                                            form.selectedTypes
                                                .map((e) => e.name)
                                                .toString()
                                        }}</small>
                                    </div>
                                    <div
                                        v-if="showOnline"
                                        :class="['flex align-center mt-1']"
                                    >
                                        <v-icon
                                            x-small
                                            color="#4b6876"
                                            class="mr-1"
                                            >{{
                                                form.online_only
                                                    ? "videocam"
                                                    : "meeting_room"
                                            }}</v-icon
                                        >
                                        <small>{{
                                            form.online_only
                                                ? "Online"
                                                : "In-studio"
                                        }}</small>
                                    </div>
                                </div>
                            </div>

                            <v-divider v-if="!cancelAvail" />

                            <div v-if="cancelAvail">
                                <div class="mb-5">
                                    <div class="caption">
                                        Cancel Availability
                                    </div>
                                    <div>
                                        {{
                                            `${form.startDate} ${
                                                form.futureEvents
                                                    ? `and all future events`
                                                    : ""
                                            }`
                                        }}
                                    </div>
                                    <small>{{
                                        `${form.startTime} to ${form.endTime}`
                                    }}</small>
                                </div>
                            </div>
                            <div class="mt-5">
                                <div
                                    class="caption"
                                    style="line-height: unset !important"
                                >
                                    Affected Lessons
                                </div>
                                <div>
                                    {{
                                        `${affectedLessonsLength} lesson(s) will be ${
                                            moveOnline
                                                ? "moved online"
                                                : "cancelled"
                                        }`
                                    }}
                                </div>
                                <small>{{
                                    `Clients ${
                                        form.skipEmail ||
                                        affectedLessonsLength == 0
                                            ? "will NOT be"
                                            : "will be"
                                    } emailed`
                                }}</small>
                            </div>
                        </v-list>
                    </v-stepper-content>
                </v-stepper>
            </template>

            <template v-slot:cancelBtn="attrs">
                <SoliBtn
                    v-if="
                        editing &&
                        step !== finalStep &&
                        step !== findStep('selectRecurring')
                    "
                    :disabled="(!saveChanges && step == 1) || !!futureConflict"
                    text="Next Step"
                    icon="chevron_right"
                    v-bind="attrs"
                    class="mt-3"
                    color="soli-blue"
                    @click="nextStep()"
                ></SoliBtn>
                <SoliBtn
                    v-if="hideSaveBtn && step == 1 && !saveChanges"
                    :text="hasLessons ? 'Time-off' : 'Delete'"
                    :icon="hasLessons ? 'event_busy' : 'delete'"
                    v-bind="attrs"
                    class="mt-3"
                    color="soli-status-red"
                    @click="handleDeleteBtn()"
                ></SoliBtn>
                <SoliBtn
                    v-if="step >= 2"
                    class="mt-3"
                    text="Back a step"
                    icon="chevron_left"
                    left
                    v-bind="attrs"
                    color="grey"
                    @click="step--"
                ></SoliBtn>
            </template>
        </SoliForm>

        <SoliChoice
            v-if="editAvailWarning"
            title="Heads up!"
            ref="editAvailWarning"
            :subtitle="`${
                editFutureAvailWarning
                    ? 'Future availabilities in this series contain '
                    : 'This availability contains '
            } lessons now or in the future. Making changes will only edit the selected date and not future events. If you'd like to edit all future availability in this series, please cancel these lessons first.`"
            button1="Okay"
            @option1="resetEditAvailWarning()"
            @reset="resetEditAvailWarning()"
        ></SoliChoice>

        <SoliChoice
            v-if="groupEventWarning"
            title="Heads up!"
            ref="groupEventWarning"
            :subtitle="`
                This availability contains group event(s) and cannot be edited at this time. Please cancel the group event before proceeding.`"
            button1="Okay"
            @option1="resetGroupEventWarning()"
            @reset="resetGroupEventWarning()"
        ></SoliChoice>
    </div>
</template>

<script>
import { path, defaultTo, isEmpty } from "ramda";
import SoliForm from "./../Base/SoliForm";
import globalMoment from "../../Mixins/GlobalMoment.js";

export default {
    mixins: [globalMoment],
    props: [
        "errors",
        "rooms",
        "type",
        "teacher",
        "avail",
        "avails",
        "calendarDate",
    ],

    data: function () {
        return {
            form: {
                id: !this.avail.newBtn
                    ? this.avail.event
                        ? this.avail.event.id
                        : null
                    : null,
                startDate: !this.avail.newBtn
                    ? new Date(this.avail.start).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                      })
                    : this.calendarDate,
                endDate: null,
                startTime: !this.avail.newBtn
                    ? new Date(this.avail.start).toLocaleTimeString("en-US", {
                          hour: "2-digit",
                          minute: "2-digit",
                      })
                    : "3:00 PM",
                endTime: !this.avail.newBtn
                    ? new Date(this.avail.end).toLocaleTimeString("en-US", {
                          hour: "2-digit",
                          minute: "2-digit",
                      })
                    : "7:30 PM",
                type: "Availability",
                duration: null,
                locations: this.avail.event
                    ? this.avail.event.extendedProps.locations
                    : this.teacher.locations.length > 1
                    ? []
                    : [this.teacher.locations[0]],
                repeating:
                    this.avail.newBtn || this.avail.newSelect
                        ? true
                        : this.avail.event.extendedProps.recurrence_id
                        ? true
                        : false,
                online_only: this.avail.event
                    ? this.avail.event.extendedProps.online_only
                    : false,
                selectedTypes: this.avail.event
                    ? this.avail.event.extendedProps.lesson_types
                    : [
                          "Assessment",
                          "Weekly Lesson",
                          "Reschedule",
                          "Drop-In",
                          "Hold",
                          "Event",
                      ],
                instruments: this.avail.event
                    ? this.avail.event.extendedProps.instruments
                    : this.teacher.instruments,
                skipEmail: false,
                futureEvents: null,
                recurrence_end: null,
                noConflict: false,
            },
            futureConflictPassed: false,
            futureConflict: null,
            singleAvail: false,
            cancelAvail: false,
            moveOnline: false,
            step: 1,
            loading: null,
            saveChanges: false,
            affectedLessons: [],
            editAvailWarning: false,
            editFutureAvailWarning: false,
            futureLessons: false,
            groupEventWarning: false,
            formError: null,
            //loadingAffectedLessons: false,
            formCopy: {},
            emailHeader: "Hi {{ parent/student }},",
            emailBody:
                "I have to cancel this lesson because [REASON]. Feel free to reschedule through our app or contact me directly. I appreciate your understanding!",
            emailBodyCopy: null,
            emailBodyRule: [
                (val) => (val || "").length > 0 || "Custom email is required",
                (val) => val.length <= 510 || "Character limit reached",
                (val) =>
                    val !== this.emailBodyCopy ||
                    "You must customize this email",
            ],
            rules: SoliForm.formRules,
            endRule: [
                (val) =>
                    (this.globalMomentGetTime(val) || "") >
                        this.globalMomentGetTime(this.form.startTime) ||
                    "End time must be greater than start time",
            ],
            editWithLessons: [
                {
                    name: "edit",
                    title: null,
                    steps: "editWithLessons",
                },
                {
                    name: "affected",
                    title: "Affected Lessons",
                },
                {
                    name: "email",
                    title: "Send Email?",
                },
                {
                    name: "confirm",
                    title: "Confirm Changes",
                },
            ],
            editWithoutLessons: [
                {
                    name: "edit",
                    title: null,
                    steps: "editWithoutLessons",
                },
                {
                    name: "selectRecurring",
                    title: "What is changing?",
                },
                {
                    name: "confirm",
                    title: "Confirm Changes",
                },
            ],
            editOneTime: [
                {
                    name: "edit",
                    title: null,
                    steps: "editWithoutLessons",
                },
                {
                    name: "confirm",
                    title: "Confirm Changes",
                },
            ],
        };
    },
    watch: {
        form: {
            handler: function (val) {
                if (
                    this.editing &&
                    JSON.stringify(val) !== JSON.stringify(this.formCopy)
                ) {
                    this.saveChanges = true;
                } else if (
                    !this.editing &&
                    val.startDate &&
                    val.startTime &&
                    val.endTime
                ) {
                    this.saveChanges = true;
                } else {
                    this.saveChanges = false;
                }
            },
            deep: true,
        },
        step(step) {
            if (step == 1) {
                this.cancelAvail = false;
                this.moveOnline = false;
                this.form.online_only = this.formCopy.originalOnlineOnly;
            }
        },
    },
    computed: {
        lessonTypes() {
            return [
                { name: "Assessment", disabled: false },
                { name: "Weekly Lesson", disabled: !this.form.repeating },
                { name: "Reschedule", disabled: false },
                { name: "Drop-In", disabled: false },
                { name: "Hold", disabled: false },
                { name: "Event", disabled: false },
            ];
        },
        removedFromSeries() {
            if (!this.avail) return null;
            return this.avail.event?.extendedProps.removed_from_series;
        },
        getRecurrenceId() {
            return defaultTo(
                null,
                path(["event", "extendedProps", "recurrence_id"], this.avail)
            );
        },
        showInstruments() {
            return this.form.originalInstruments
                ? this.form.instruments.length !=
                      this.formCopy.originalInstruments.length
                : false;
        },
        showSelectedTypes() {
            return this.form.originalSelectedTypes
                ? this.form.selectedTypes.length !=
                      this.formCopy.selectedTypes.length
                : false;
        },
        showOnline() {
            return this.formCopy.originalOnlineOnly
                ? this.form.online_only !== this.formCopy.originalOnlineOnly
                : true;
        },
        affectedLessonsLength() {
            return this.affectedLessons
                ? this.affectedLessons.filter((e) => !e.cancelled).length
                : 0;
        },
        whichSteps() {
            return this.affectedLessonsLength > 0
                ? this.editWithLessons
                : this.singleAvail
                ? this.editOneTime
                : this.editWithoutLessons;
        },
        getStepperTitle() {
            return this.whichSteps[this.step - 1].title;
        },
        hideSaveBtn() {
            return !this.editing
                ? false
                : this.step !== this.finalStep
                ? true
                : false;
        },
        showDelete() {
            return this.editing && !this.saveChanges;
        },
        hasLessons() {
            return this.avail.newBtn
                ? null
                : this.avail.event
                ? this.avail.event.extendedProps.lessons.length
                    ? true
                    : false
                : null;
        },
        editing() {
            return this.form.id !== null;
        },
        getSelection() {
            if (this.step == this.finalStep) return [];
            return [
                {
                    name: "date",
                    base: true,
                    selection: {
                        data: () =>
                            this.globalMomentLongDate(this.form.startDate),
                        hide: !this.form.startDate,
                    },
                },
                {
                    name: "recurrenceEnd",
                    base: true,
                    selection: {
                        data: () => `until ${this.getRecurrenceEnd}`,
                        hide: !this.getRecurrenceEnd || this.removedFromSeries,
                    },
                },
                {
                    name: "repeating",
                    base: true,
                    selection: {
                        data: () => this.getRepeating,
                        hide: this.getRecurrenceEnd && !this.removedFromSeries,
                    },
                },
            ];

            /*this.form.startDate
                ? [`${this.globalMomentLongDate(this.form.startDate)}${
                      this.getRecurrenceEnd
                          ? ` · until ${this.getRecurrenceEnd}`
                          : ` · ${this.getRepeating}`
                  }`]
                : [""];*/
        },
        getRepeating() {
            if (this.removedFromSeries) return "Removed from series";
            if (this.cancelAvail) return "This event";
            return this.form.repeating ? "Repeats Weekly" : "Does not repeat";
        },
        getRecurrenceEnd() {
            return path(
                ["event", "extendedProps", "recurrence_end"],
                this.avail
            )
                ? this.globalMomentDate(
                      this.avail.event.extendedProps.recurrence_end
                  )
                : null;
        },
        finalStep() {
            return this.whichSteps.length;
        },
        progressVal() {
            return (this.step * 100) / this.finalStep;
        },
        finalStep() {
            return this.whichSteps.length;
        },
        getFormTitle() {
            return this.step == this.finalStep
                ? "Confirm Changes"
                : this.editing
                ? this.cancelAvail
                    ? this.hasLessons
                        ? "Cancel Availability"
                        : "Delete Availability"
                    : "Edit Availability"
                : "Add Availability";
        },
    },
    methods: {
        updateRepeating(val) {
            if (val) return;
            let getWeeklyLesson =
                this.form.selectedTypes.indexOf("Weekly Lesson");
            if (getWeeklyLesson >= 0) {
                this.form.selectedTypes.splice(getWeeklyLesson, 1);
            }
        },
        nextStep() {
            if (this.getStepperTitle == "Send Email?" && !this.form.skipEmail) {
                this.$refs.email.focus();
                this.$refs.email.validate();
                this.$refs.email.blur();
                if (!this.$refs.email.validate()) return;
            }
            this.step++;
            if (this.hasLessons) this.getAffectedLessons();
        },
        resetEditAvailWarning() {
            this.$refs.editAvailWarning.dialog = false;
            this.editFutureAvailWarning = false;
            setTimeout(() => {
                this.editAvailWarning = false;
            }, 300);
        },
        resetGroupEventWarning() {
            this.$emit("reset");
        },
        handleDeleteBtn() {
            this.step++;
            this.cancelAvail = true;
            this.futureConflict = false;
            this.getAffectedLessons();
        },
        findStep(search) {
            return this.whichSteps.find((e) => e.name == search)
                ? this.whichSteps.findIndex((e) => e.name == search) + 1
                : "";
        },
        handleSelectFuture() {
            if (!this.editing) return;
            this.form.futureEvents = true;
        },
        handleSelectSingle() {
            if (!this.editing) return;
            this.form.futureEvents = false;
        },
        getAffectedLessons() {
            if (!this.editing) return;
            this.affectedLessons =
                this.avail.event.extendedProps.lessons.filter(
                    (e) =>
                        this.globalMoment24Time(e.starts_at) <
                            this.globalMoment24Time(
                                new Date(
                                    this.form.startDate +
                                        " " +
                                        this.form.startTime
                                )
                            ) ||
                        this.globalMoment24Time(e.ends_at) >
                            this.globalMoment24Time(
                                new Date(
                                    this.form.startDate +
                                        " " +
                                        this.form.endTime
                                )
                            ) ||
                        this.cancelAvail ||
                        this.moveOnline
                );
            // why
            /*if (
                !!this.affectedLessons.find((e) =>
                    e.group_event ? e.group_event.in_studio_only : null
                ) &&
                this.form.futureEvents
            ) {
                this.groupEventWarning = true;
                return;
            }*/
            if (this.affectedLessons.length && this.step == 2) {
                this.form.futureEvents = false;
                this.editAvailWarning = true;
            }
        },
        reset() {
            this.$emit("reset", true);
            this.futureConflict = null;
            this.futureLessons = false;
        },
        editEvent() {
            if (!this.$refs.form.$refs.form.validate()) return;

            let eventData = {
                id: this.form.id,
                starts_at: this.form.startDate + " " + this.form.startTime,
                ends_at: this.form.startDate + " " + this.form.endTime,
                //repeating: this.form.repeating,
                user_id: this.teacher.id,
                instrument_ids: this.form.instruments.map((inst) => inst.id), //this.teacher.instruments.map((inst) => inst.id),
                //online_only: this.form.online_only,
                location_ids: this.form.locations.map((e) => e.id),
                futureEvents: !!this.form.futureEvents,
                cancelled: this.cancelAvail,
                recurrence_end: this.form.recurrence_end,
                recurrence_id: this.getRecurrenceId,
                noConflict: this.form.noConflict,
                customEmail: this.form.skipEmail ? null : this.emailBody,
                lesson_types: this.form.selectedTypes,
                online_only: this.form.online_only,
            };

            this.$inertia.put(`/availability/${eventData.id}`, eventData, {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => {
                    this.$emit("success");
                    setTimeout(() => {
                        this.$emit("reset", false);
                    }, 300);
                },
                onError: (errors) => {
                    this.$refs.form.loading = false;

                    if (Object.keys(this.errors).includes("contactSupport")) {
                        this.futureConflict = null;
                    } else if (errors[0]?.includes("roomConflict")) {
                        this.futureConflict = this.errors;
                    } else {
                        this.formError = Object.values(errors)[0];
                    }
                },
            });
        },
        addEvent() {
            if (!this.$refs.form.$refs.form.validate()) return;

            let eventData = {
                starts_at: this.form.startDate + " " + this.form.startTime,
                ends_at: this.form.startDate + " " + this.form.endTime,
                online_only: this.form.online_only,
                repeating: this.form.repeating,
                futureEvents: this.form.repeating,
                user_id: this.teacher.id,
                instrument_ids: this.form.instruments.map((inst) => inst.id),
                recurrence_end: this.form.recurrence_end,
                recurrence_id: this.getRecurrenceId,
                noConflict: this.form.noConflict,
                location_ids: this.form.locations.map((e) => e.id),
                lesson_types: this.form.selectedTypes,
                cancelled: false,
                rrule_freq: "WEEKLY",
                rrule_int: "1",
            };

            this.$inertia.post("/availability", eventData, {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => {
                    this.$emit("success");
                    setTimeout(() => {
                        this.$emit("reset", false);
                    }, 300);
                },
                onError: (errors) => {
                    this.$refs.form.loading = false;

                    if (Object.keys(this.errors).includes("contactSupport")) {
                        this.futureConflict = null;
                    } else if (errors[0]?.includes("roomConflict")) {
                        this.futureConflict = this.errors;
                    } else {
                        this.formError = Object.values(errors)[0];
                    }
                },
            });
        },
    },
    mounted() {
        if (
            !this.form.id &&
            this.form.startDate &&
            this.form.startTime &&
            this.form.endTime
        ) {
            this.saveChanges = true;
        }
        if (!path(["event", "extendedProps", "recurrence_id"], this.avail)) {
            this.singleAvail = true;
        }
        if (this.avail.newBtn || path(["oldEvent"], this.avail)) {
            this.getAffectedLessons();
            if (this.avail.oldEvent) {
                this.saveChanges = true;
                this.formCopy = {
                    originalStartDate: new Date(
                        this.avail.oldEvent.start
                    ).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                    }),
                    originalStartTime: new Date(
                        this.avail.oldEvent.start
                    ).toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                    }),
                    originalEndTime: new Date(
                        this.avail.oldEvent.end
                    ).toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                    }),
                    originalInstruments: this.teacher.instruments,
                    originalSelectedTypes: this.avail.lesson_types,
                    originalOnlineOnly: this.avail.online_only,
                };
            }
        } else {
            this.formCopy = {
                originalStartDate: new Date(
                    this.avail.start
                ).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                }),
                originalStartTime: new Date(
                    this.avail.start
                ).toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                }),
                originalEndTime: new Date(this.avail.end).toLocaleTimeString(
                    "en-US",
                    {
                        hour: "2-digit",
                        minute: "2-digit",
                    }
                ),
                originalInstruments: this.teacher.instruments,
                originalSelectedTypes: this.lessonTypes,
                originalOnlineOnly: this.avail.event?.extendedProps.online_only,
            };
        }

        this.emailBodyCopy = JSON.parse(JSON.stringify(this.emailBody));
    },
};
</script>
